import React, {Component} from "react"
import styled from "styled-components"
import PropTypes from "prop-types"
import {withRouter} from "react-router"
import {omitBy, isNil, round} from "lodash"

import id from "@irati/utils/id"
import {RouterLink} from "@irati/ui/src/Link"
import {injectRouteWithQuery} from "@irati/utils/helpers/routing"
import {AnalysisContext} from "@irati/insights/context"
import {greys} from "@irati/ui/src/global/colors"
import {doIHavePermission} from "@irati/utils/permissions/HasPermissionTo"
import auth from "@irati/auth"

import BaseCell from "./BaseCell"
import {CELL_WIDTH} from "../../utils/constants/heatmap"
import sitemap from "../../routes/sitemap"
import {ChangeOverTime} from "../../ui/PillBadge"

const getColors = (opacity) => [
  `rgba(0,189,179, ${opacity})`, // dark green
  `rgba(141,207,208, ${opacity})`, // light green
  `rgba(204,204,204, ${opacity})`, // grey
  `rgba(244,188,179, ${opacity})`, // light red
  `rgba(241,110,90, ${opacity})` // dark red
]

const style = `
  height:50px;
  min-width:${CELL_WIDTH}px;
  display:flex;
  flex:1;
  justify-content: center;
  align-items: center;
  border-radius: 4%;
  font-family: Lato,sans-serif;
  box-sizing:border-box;
`

const StyledRouterLink = styled(RouterLink)`
  background-color: ${({color}) => color};
  ${style}
  :hover {
    text-decoration: none;
    box-shadow:inset 0 0 0 100px rgba(0,0,0,0.2);
  }
  :active {
    border-top: 2px solid rgba(0,0,0, 0.4);
  }
  display: flex;
  flex-direction: column;
`

const EmptyScore = styled.span`
  background-color: ${greys.lightest};
  ${style}
`

class Score extends Component {
  static propTypes = {
    average: PropTypes.number.isRequired,
    standardDeviation: PropTypes.number.isRequired,
    stat: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    isHighlighted: PropTypes.bool,
    historicalLoading: PropTypes.bool,
    showCOT: PropTypes.bool
  }

  static defaultProps = {
    isHighlighted: false,
    historicalLoading: false,
    showCOT: false
  }

  getColor = (opacity = 1) => {
    const {average, stat: {averageScore: score}, standardDeviation} = this.props
    const greatScore = average + standardDeviation
    const badScore = average - standardDeviation
    const gap = (greatScore - badScore) / 11
    const okayScore = average + gap
    const poorScore = average - gap
    const colors = getColors(opacity)

    if (score > greatScore) return colors[0]
    if (score > okayScore) return colors[1]
    if (score > poorScore) return colors[2]
    if (score > badScore) return colors[3]

    return colors[4]
  }

  render() {
    const idNamespace = id("Score", this.props)
    const {stat, match, isHighlighted, historicalLoading, showCOT} = this.props
    const {averageScore, driverId, teamId, historicalAverageScore} = stat

    const hasMinDif = Math.abs(averageScore - historicalAverageScore) >= 0.1
    const showCOTFeature = doIHavePermission(["insights.changeOverTime"], auth.apps())

    const disabled = averageScore === 0
    return (
      <BaseCell>
        <AnalysisContext.Consumer>
          {({filters}) => {
            return !disabled ? (
              <StyledRouterLink
                id={id("link", idNamespace)}
                disabled={disabled}
                to={injectRouteWithQuery(sitemap.analysis.url, {
                  params: {...match.params},
                  queryParams: omitBy({
                    ...filters,
                    driverId,
                    teamId
                  }, isNil)
                })}
                color={this.getColor(isHighlighted && !showCOT ? 1 : 0.2)}>
                {round(averageScore, 1).toFixed(1)}
                {!historicalLoading && hasMinDif && showCOTFeature && showCOT && (
                  <ChangeOverTime currentScore={averageScore} historicalScore={historicalAverageScore} hideTooltip />
                )
                }
              </StyledRouterLink>
            ) : <EmptyScore id={id("empty", idNamespace)}> - </EmptyScore>
          }}
        </AnalysisContext.Consumer>
      </BaseCell>
    )
  }
}

export default withRouter(Score)
